import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Outlet, useNavigate } from "react-router-dom";
import { mdiWallet } from "@mdi/js";
import Icon from "@mdi/react";
import clsx from "clsx";
import { useToggle } from "usehooks-ts";

import Button from "components/Button";
import Header from "components/Header";
import Input from "components/Input";
import Modal from "components/Modal";
import { EyeOffOutlineSVGIcon, EyeOutlineSVGIcon, WalletSVGIcon } from "components/SVGIcons";
import TabSwitch from "components/TabSwitch";
import { BROKER_TAB_ITEMS } from "constants/haulpay-broker";
import { routes } from "constants/routes";
import { formatNumber } from "helpers/formatNumber";
import { showErrorMessage, showSuccessMessage } from "helpers/toastMessage";
import { useUserFeature } from "hooks/useUserFeature";
import MainLayout from "layouts/Main";
import { getWalletOnboardingUrlAPI, makeDepositAPI } from "services/api/haulpay";
import { AppDispatch } from "store";
import { getWalletBalance } from "store/home/actions";
import { selectWalletBalance } from "store/home/selector";
import { selectUserDetails } from "store/user/selector";
import styles from "./style.module.scss";

export default function BrokerWallet() {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const { isAdmin } = useUserFeature();
  const [isModalOpen, toggleModal] = useToggle();
  const [isConfirmModalOpen, toggleConfirmModal] = useToggle();

  const [isShow, setIsShow] = useState(true);
  const [depositAmount, setDepositAmount] = useState<number>();

  const userDetails = useSelector(selectUserDetails);
  const walletBalance = useSelector(selectWalletBalance);

  const fetchWalletBalance = async () => {
    dispatch(getWalletBalance());
  };

  const submitDeposit = async () => {
    const formData = {
      company_id: userDetails.factoring_id as string,
      deposit_type: "self_finance_debit",
      deposit_amount: depositAmount
    };

    try {
      await makeDepositAPI(formData as any);
      toggleModal();
      showSuccessMessage("Deposit has been created successfully.");
      fetchWalletBalance();
      navigate(routes.HAULPAY_BROKER_WALLET);
    } catch {
      showErrorMessage("Failed to create deposit.");
    }
  };

  const getWalletOnboardingUrl = async () => {
    try {
      const response = await getWalletOnboardingUrlAPI();
      if (response.data) {
        window.location.replace(response.data.url);
      }
    } catch (error: any) {
      showErrorMessage(error.message);
    }
  };

  useEffect(() => {
    if (isAdmin) {
      fetchWalletBalance();
    } else {
      toggleConfirmModal();
    }
  }, [isAdmin]);

  return (
    <MainLayout
      desktopTitle="My Wallet"
      customHeader={<Header label="My Wallet" goBackPath={routes.HAULPAY} isHideDesktop />}
      className={styles.brokerWalletContainer}>
      <div className={styles.cardWrapper}>
        <div className={styles.cardContent}>
          <div className={styles.currentBalance}>
            <span className="text-sm desktop:text-base">Current Balance</span>
            <div className="flex items-end">
              <span className="text-3xl font-bold">
                {isShow ? formatNumber(walletBalance?.balance ?? 0, 2).split(".")[0] : "$***"}
              </span>
              <span className="text-xl">{`.${isShow ? formatNumber(walletBalance?.balance ?? 0, 2).split(".")[1] : "**"}`}</span>
              <button onClick={() => setIsShow(!isShow)}>
                {isShow ? (
                  <EyeOffOutlineSVGIcon className="ml-3.5 mb-1.5" />
                ) : (
                  <EyeOutlineSVGIcon className="ml-3.5 mb-2" />
                )}
              </button>
            </div>
          </div>

          <div className={styles.description}>
            <div className={styles.item}>
              <span>Latest Statement Date</span>
              <span className="text-lg font-medium">6/24/2024</span>
            </div>

            <div className={styles.item}>
              <span>Total Invoices</span>
              <span className="text-lg font-medium">20</span>
            </div>

            <div className={styles.item}>
              <span>Pending</span>
              <span className="text-lg font-medium">
                {walletBalance ? formatNumber(walletBalance.pending ?? 0, 2) : "-"}
              </span>
            </div>
          </div>

          <button className={styles.addMoneyBackground} onClick={toggleModal}>
            <span className={styles.addMoneyButton}>ADD MONEY</span>
          </button>
        </div>

        <div className={styles.pendingBalance}>
          <span className="text-sm">Pending</span>
          {walletBalance ? formatNumber(walletBalance.pending ?? 0, 2) : "-"}
        </div>

        <WalletSVGIcon className={styles.walletIcon} />
      </div>

      <TabSwitch options={BROKER_TAB_ITEMS} />

      <Outlet />

      <Modal
        isOpen={isModalOpen}
        closeModal={toggleModal}
        wrapperClass="!my-auto desktop:!min-h-fit text-sm"
        dialogClass="!bg-foreground !p-4 !border !border-card !overflow-y-auto gap-2.5"
        closeClass="!top-24 !right-6 !bg-transparent !text-text">
        <span className="text-xl font-bold text-primary">Make a Deposit</span>
        <div className="mt-4 mb-2">
          <Input
            type="number"
            inputMode="numeric"
            label="&nbsp;Amount"
            placeholder="$0.00"
            onChange={(e) => setDepositAmount(parseFloat(e.target.value))}
            inputClass="!border !border-subtitle"
          />
        </div>
        <div className="flex gap-4 justify-between *:w-20 *:text-sm *:rounded-full *:h-9">
          <Button variant="outline" onClick={toggleModal}>
            Cancel
          </Button>
          <Button
            className={clsx(
              !depositAmount
                ? "!bg-subtitle !ring-subtitle cursor-not-allowed"
                : "bg-gradient-to-r from-primary to-secondary"
            )}
            {...(depositAmount && { disabled: true })}
            onClick={submitDeposit}>
            Deposit
          </Button>
        </div>
      </Modal>

      <Modal
        isOpen={isConfirmModalOpen}
        closeModal={toggleConfirmModal}
        isDisableCloseModal
        wrapperClass="!my-auto desktop:!min-h-fit text-sm"
        dialogClass="!bg-foreground !p-6 desktop:!p-10 !border !border-card !overflow-y-auto gap-2.5 mx-auto"
        closeClass="!hidden">
        <Icon path={mdiWallet} className="mx-auto size-18 text-primary" />

        <span className="mt-4 mb-2 desktop:text-lg">
          Please first complete the onboarding process.
        </span>

        <Button type="button" onClick={getWalletOnboardingUrl}>
          Continue
        </Button>
      </Modal>
    </MainLayout>
  );
}
